<template>
  <div class="pb-2">
    <div class="card mx-2 mb-2">
      <label class="card-header font-weight-bold">Show Data For:</label>
      <div class="card-body p-0 symbol-container border-right bg-light">
        <!-- Observations Option -->
        <button
          type="button"
          class="symbol symbol-observations border-bottom"
          :class="{
            active: resultType === 'observation',
          }"
          @click="getObservationLocationResults()"
        >
          <i
            class="symbol-radio ml-1 my-2"
            :class="{
              'fas fa-circle': resultType === 'observation',
              'far fa-circle': resultType !== 'observation',
            }"
          ></i>
          <i class="fa fa-map-marker-alt ml-1 my-2"></i>
          <span class="ml-1">Number of Observations</span>
          <span class="ml-auto symbol-chevron px-1">
            <i class="fa fa-chevron-right"></i>
          </span>
        </button>

        <!-- Datasheets Option -->
        <button
          type="button"
          class="symbol symbol-datasheets border-bottom"
          :class="{ active: resultType === 'datasheet_usage' }"
          @click="getDatasheetUsageLocationResults()"
        >
          <i
            class="symbol-radio ml-1 my-2"
            :class="{
              'fas fa-circle': resultType === 'datasheet_usage',
              'far fa-circle': resultType !== 'datasheet_usage',
            }"
          ></i>
          <i class="fa fa-rectangle-list ml-1 my-2"></i>
          <span class="ml-1">Datasheet Used</span>
          <span class="ml-auto symbol-chevron px-1">
            <i class="fa fa-chevron-right"></i>
          </span>
        </button>

        <!-- Datasheet Records grouped by Datasheet -->
        <div
          v-for="datasheet in sortedDatasheets"
          :key="`group-${datasheet.id}`"
        >
          <!-- Datasheet Header (clickable to select entire datasheet) -->
          <button
            type="button"
            class="symbol symbol-datasheet-header border-bottom font-weight-bold"
            :class="{
              active: resultId === datasheet.id,
            }"
            @click="getDatasheetLocationResults(datasheet)"
          >
            <i
              class="symbol-radio ml-1 my-2"
              :class="{
                'fas fa-circle': resultId === datasheet.id,
                'far fa-circle': !(resultId === datasheet.id),
              }"
            ></i>
            <i class="fa fa-rectangle-list ml-1 my-2"></i>
            <i class="fa fa-lock ml-1 my-2" v-if="datasheet.isPrivate"></i>
            <span class="ml-1">{{ datasheet.name }}</span>
            <span class="ml-auto symbol-chevron px-1">
              <i class="fa fa-chevron-right"></i>
            </span>
          </button>

          <!-- Records within this Datasheet -->
          <template v-for="record in getDatasheetRecords(datasheet)">
            <button
              v-if="['number'].includes(record.recordType)"
              :key="`record-${record.id}`"
              type="button"
              class="symbol symbol-datasheet-record border-bottom"
              :class="{ active: resultId === record.id }"
              @click="getDatasheetRecordLocationResults(record)"
            >
              <i
                class="symbol-radio ml-1 my-2"
                :class="{
                  'fas fa-circle': resultId === record.id,
                  'far fa-circle': resultId !== record.id,
                }"
              ></i>
              <i class="fa fa-hashtag ml-3 my-2"></i>
              <i class="fa fa-lock ml-1 my-2" v-if="record.isPrivate"></i>
              <span class="ml-1">{{ record.label }}</span>
              <span class="ml-auto symbol-chevron px-1">
                <i class="fa fa-chevron-right"></i>
              </span>
            </button>
            <button
              v-if="['dropdown', 'radio'].includes(record.recordType)"
              :key="`record-${record.id}`"
              type="button"
              class="symbol symbol-datasheet-record border-bottom"
              :class="{ active: resultId === record.id }"
              @click="getDatasheetRecordLocationResults(record)"
            >
              <i
                class="symbol-radio ml-1 my-2"
                :class="{
                  'fas fa-circle': resultId === record.id,
                  'far fa-circle': resultId !== record.id,
                }"
              ></i>
              <i class="fa fa-list ml-3 my-2"></i>
              <i class="fa fa-lock ml-1 my-2" v-if="record.isPrivate"></i>
              <span class="ml-1">{{ record.label }}</span>
              <span class="ml-auto symbol-chevron px-1">
                <i class="fa fa-chevron-right"></i>
              </span>
            </button>

            <!-- Organism record with nested sub-records -->
            <div
              v-if="['organism'].includes(record.recordType)"
              :key="`organism-${record.id}`"
            >
              <!-- Organism Header (non-clickable, just shows the organism name) -->
              <div class="pl-3 border-bottom font-weight-bold">
                <i class="fa fa-lock mr-2" v-if="record.isPrivate"></i>
                <span v-if="record.allowAnyOrganism">Any Organism</span>
                <span v-else-if="record.organisms.length === 1">{{
                  record.organisms[0].customName
                }}</span>
                <span v-else>Multiple Organisms</span>
              </div>

              <!-- Sub-records within this organism -->
              <template v-for="subRecord in getOrganismSubRecords(record)">
                <button
                  v-if="subRecord.recordType === 'number'"
                  :key="`subrecord-${subRecord.id}`"
                  type="button"
                  class="symbol symbol-organism-subrecord border-bottom"
                  :class="{
                    active: resultId === subRecord.id,
                  }"
                  @click="getDatasheetRecordLocationResults(subRecord)"
                >
                  <i
                    class="symbol-radio ml-1 my-2"
                    :class="{
                      'fas fa-circle': resultId === subRecord.id,
                      'far fa-circle': resultId !== subRecord.id,
                    }"
                  ></i>
                  <i class="fa fa-hashtag ml-4 my-2"></i>
                  <i
                    class="fa fa-lock ml-1 my-2"
                    v-if="subRecord.isPrivate"
                  ></i>
                  <span class="ml-1">{{ subRecord.label }}</span>
                  <span class="ml-auto symbol-chevron px-1">
                    <i class="fa fa-chevron-right"></i>
                  </span>
                </button>
                <button
                  v-if="['dropdown', 'radio'].includes(subRecord.recordType)"
                  :key="`subrecord-${subRecord.id}`"
                  type="button"
                  class="symbol symbol-organism-subrecord border-bottom"
                  :class="{
                    active: resultId === subRecord.id,
                  }"
                  @click="getDatasheetRecordLocationResults(subRecord)"
                >
                  <i
                    class="symbol-radio ml-1 my-2"
                    :class="{
                      'fas fa-circle': resultId === subRecord.id,
                      'far fa-circle': resultId !== subRecord.id,
                    }"
                  ></i>
                  <i class="fa fa-list ml-4 my-2"></i>
                  <i
                    class="fa fa-lock ml-1 my-2"
                    v-if="subRecord.isPrivate"
                  ></i>
                  <span class="ml-1">{{ subRecord.label }}</span>
                  <span class="ml-auto symbol-chevron px-1">
                    <i class="fa fa-chevron-right"></i>
                  </span>
                </button>
              </template>
            </div>
          </template>
        </div>
      </div>
      <div class="card-footer p-1 text-right">
        <b-form-checkbox
          :checked="displayNoData"
          @change="LOCATION_SET_DISPLAY_NO_DATA"
          switch
          size="sm"
        >
          <span>Display Locations without data</span>
        </b-form-checkbox>
        <div class="text-muted small">
          <span
            >{{ locationsWithData }} of {{ totalLocations }} locations have
            data</span
          >
        </div>
      </div>
    </div>

    <!-- Loading State -->

    <MapAggregationPicker />

    <!-- Discrete Data Legend (for observations and numeric data) -->
    <DiscreteDataLegend class="m-2" />
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations, mapActions } from "vuex";
import DiscreteDataLegend from "./DiscreteDataLegend.vue";
import MapAggregationPicker from "./MapAggregationPicker.vue";

export default {
  name: "SymbologyPicker",
  components: {
    DiscreteDataLegend,
    MapAggregationPicker,
  },
  data() {
    return {
      showingDatasheetUsage: false,
    };
  },
  computed: {
    ...mapGetters({
      publishedDatasheets: "project/datasheets/publishedDatasheets",
      allLocations: "project/map/locations",
      projectId: "project/get/id",
      isManager: "project/get/isManager",
    }),
    ...mapState({
      resultType: (state) => state.project.map.resultType,
      displayNoData: (state) => state.project.map.displayNoData,
      resultId: (state) => state.project.map.resultId,
    }),
    /**
     * Sort datasheets alphabetically by name
     */
    sortedDatasheets() {
      if (!this.publishedDatasheets) return [];
      return [...this.publishedDatasheets]
        .filter((datasheet) => {
          // Show all datasheets if user is a manager
          if (this.isManager) return true;
          // Otherwise, hide private datasheets
          return !datasheet.isPrivate;
        })
        .sort((a, b) => {
          return a.name.localeCompare(b.name);
        });
    },
    /**
     * Calculate total number of locations
     */
    totalLocations() {
      if (!this.allLocations || !Array.isArray(this.allLocations)) return 0;
      return this.allLocations.length;
    },
    /**
     * Calculate number of locations that have data for the current selection
     */
    locationsWithData() {
      if (!this.allLocations || !Array.isArray(this.allLocations)) return 0;

      return this.allLocations.filter((loc) => {
        // For numeric or categorical records
        if (this.resultId) {
          return loc.results && loc.results[this.resultId];
        }
        // For datasheet-level data
        else if (this.resultId) {
          return loc.results && loc.results[this.resultId];
        }
        // For datasheet usage view
        else if (this.showingDatasheetUsage) {
          return (
            loc.datasheetUsage && loc.datasheetUsage.most_frequent_datasheet_id
          );
        }
        // For observations view
        else {
          return loc.observationsTotal && loc.observationsTotal > 0;
        }
      }).length;
    },
  },
  methods: {
    ...mapActions({
      getObservationLocationResults:
        "project/map/getObservationLocationResults",
      getDatasheetUsageLocationResults:
        "project/map/getDatasheetUsageLocationResults",
      getDatasheetLocationResults: "project/map/getDatasheetLocationResults",
      getDatasheetRecordLocationResults:
        "project/map/getDatasheetRecordLocationResults",
    }),
    ...mapMutations({
      LOCATION_SET_DISPLAY_NO_DATA: "project/map/LOCATION_SET_DISPLAY_NO_DATA",
    }),
    /**
     * Get records for a datasheet, filtered and sorted by orderNumber
     */
    getDatasheetRecords(datasheet) {
      if (!datasheet || !datasheet.records) return [];

      // Only show record types supported by the backend controller
      // The backend uses recordType property (not type), and checks for 'dropdown' or 'radio' for categorical
      const supportedTypes = ["number", "dropdown", "radio", "organism"];

      return datasheet.records.filter((record) => {
        // Filter by supported recordType
        if (!supportedTypes.includes(record.recordType)) return false;
        // Show all records if user is a manager
        if (this.isManager) return true;
        // Otherwise, hide private records
        return !record.isPrivate;
      });
    },
    /**
     * Get sub-records from an organism record that are supported types
     */
    getOrganismSubRecords(organismRecord) {
      if (!organismRecord || !organismRecord.records) return [];

      // Normalize records to array
      const recordsArray = Array.isArray(organismRecord.records)
        ? organismRecord.records
        : Object.keys(organismRecord.records).map(
            (key) => organismRecord.records[key],
          );

      if (recordsArray.length === 0) return [];

      const supportedTypes = ["number", "dropdown", "radio"];
      return recordsArray
        .filter((subRecord) => {
          if (!supportedTypes.includes(subRecord.recordType)) return false;
          if (this.isManager) return true;
          return !subRecord.isPrivate;
        })
        .sort((a, b) => a.orderNumber - b.orderNumber);
    },
  },
  watch: {
    projectId(newProjectId, oldProjectId) {
      // When project changes, reset selections to Observations
      if (oldProjectId && newProjectId && oldProjectId !== newProjectId) {
        console.log(
          "Project changed in SymbologyPicker, resetting to Observations",
        );
        this.showingDatasheetUsage = false;
        this.getObservationLocationResults();
      }
    },
  },
};
</script>

<style scoped>
.symbol-container {
  max-height: 180px;
  overflow-y: auto;
  overflow-x: hidden;
}
.symbol {
  display: flex;
  align-items: center;
  width: 100%;
  text-align: left;
}
.symbol,
.symbol-chevron {
  transition: background-color 0.2s;
}
.symbol-chevron {
  background-color: rgba(255, 255, 255, 1);
  align-self: stretch;
  display: flex;
  align-items: center;
}
.symbol:hover {
  background-color: rgb(241, 246, 214);
}
.symbol:hover > .symbol-chevron {
  background-color: rgba(135, 152, 63, 0.35);
}
.symbol:hover:not(.active) > .symbol-radio.fa-circle::before {
  content: "\f192" !important;
}
.symbol:hover:not(.active) > .symbol-radio.fas::before {
  font-weight: 400 !important;
}
.symbol:hover:not(.active) > .symbol-radio.far::before {
  font-weight: 900 !important;
}

.symbol.active {
  background-color: rgb(221, 229, 182);
}
.symbol-observations,
.symbol-datasheets {
  font-weight: bold;
}

.symbol-datasheet-header {
  position: sticky;
  top: 0;
  z-index: 10;
  background-color: #f1f3f5;
}
.symbol-datasheet-record {
  display: flex;
  align-items: center;
  width: 100%;
  text-align: left;
}

.symbol-organism-subrecord {
  display: flex;
  align-items: center;
  width: 100%;
  text-align: left;
  background-color: #f8f9fa;
}

.symbol-organism-subrecord:hover {
  background-color: rgb(221, 229, 182);
}

.symbol-organism-subrecord.active {
  background-color: rgba(135, 152, 63, 0.2);
}
</style>
